import { capitalize } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { Jackpot, JackpotProduct } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import { useModal } from '../../contexts/ModalContext';
import { createDescriptionColumn, createEditColumn, createIsActiveColumn, HasIsActive } from '../../helpers/columns';
import { defaultColumnsJackpots } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn } from '../../utils';
import IconRenderer from '../atoms/IconRenderer';
import { ICON_GROUP } from '../../constants/enums';
import TableIconContainer from '../atoms/TableIconContainer';
import ConfirmationModal from '../modals/ConfirmationModal';
import TableTemplate from '../templates/TableTemplate';
import { MESSAGES } from '../../constants/messages';
import { JACKPOT_API_SERVICE } from '../../constants/jackpot';

type Props = {
  product: JackpotProduct;
};

const JackpotPage = ({ product }: Props) => {
  const apiService = JACKPOT_API_SERVICE[product];
  const iconType = product === 'casino' ? ICON_GROUP.CASINO_JACKPOT : ICON_GROUP.JACKPOT;

  const productQueryParams = useMemo(() => ({ product }), [product]);
  const {
    data: jackpotData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<Jackpot>(
    'jackpots',
    {
      page: 1,
      limit: 25,
      sort: 'createdAt',
      order: 'DESC',
    },
    undefined,
    apiService,
    undefined,
    productQueryParams
  );

  const { handleSort } = useSort(changeQuery);
  const { updateData } = useMutateData('jackpots', [QUERY_KEYS.jackpots], apiService);
  const { openModal, closeModal } = useModal();

  const handleIsActiveChange = (id: string, data: Partial<HasIsActive>) => {
    // `data.isActive` corresponds to `e.target.checked` from the Switch component.
    // It represents the new state the user is trying to set.
    const confirmMessage = data.isActive ? MESSAGES.ACTIVATE_JACKPOT : MESSAGES.DEACTIVATE_JACKPOT;

    openModal(
      <ConfirmationModal
        title={confirmMessage}
        actionButtonLabel='Confirm'
        handleConfirm={() => updateData(id, { ...data, product })}
        closeModal={closeModal}
        actionButtonClose='Cancel'
      />
    );
  };

  const columns: GridColDef[] = [
    createColumn('id', 'ID', { sortable: false }),
    createColumn('icon', 'Icon', {
      sortable: false,
      renderCell: (params: GridRenderCellParams<Jackpot>) => {
        return (
          <TableIconContainer>
            <IconRenderer name={params.row.icon} type={iconType} />
          </TableIconContainer>
        );
      },
    }),
    createColumn('type', 'Type', {
      alwaysVisible: true,
      valueGetter: (_value, row: Jackpot) => capitalize(row.type),
    }),
    createColumn('name', 'Name', {
      minWidth: 150,
      alwaysVisible: true,
    }),
    createIsActiveColumn({
      alwaysVisible: true,
      updateData: handleIsActiveChange,
    }),
    createColumn('winningAmount', 'Jackpot winning amount', {
      minWidth: 175,
      sortable: false,
      valueGetter: (_value, row: Jackpot) => `${row.winningLow} - ${row.winningHigh}`,
    }),
    createColumn('stakeRanges', 'Bet pay in amount', {
      minWidth: 175,
      sortable: false,
      flex: 2,
      valueGetter: (_value, row: Jackpot) =>
        row.stakeRanges.map((stakeRange: Jackpot['stakeRanges'][number]) => stakeRange.name).join(', '),
    }),
    createColumn('winningTickets', product === 'casino' ? 'Winning spins' : 'Winning bets', {
      minWidth: 175,
      sortable: false,
      flex: 2,
      valueGetter: (_value, row: Jackpot) => (row.winningTickets ?? []).filter(Boolean).join(', '),
    }),
    createDescriptionColumn(),
    createEditColumn<Jackpot>({ formType: 'jackpot', formProps: { product } }),
  ];

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  return (
    <TableTemplate
      rows={jackpotData?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={jackpotData?.count || 0}
      handleSearch={handleSearch}
      handleSort={handleSort}
      changeQuery={changeQuery}
      defaultVisibleColumns={defaultColumnsJackpots}
      handlePaginationModelChange={updateQueryParams}
      createItemBtnLabel='Add new jackpot pool'
      formType='jackpot'
      formProps={{ product }}
      filterType='jackpot'
      filterProps={{ product }}
    />
  );
};

export default JackpotPage;
